import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, LayoutTemplate, KeyRound, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSecuritySettings } from '@/hooks/useSecuritySettings';

interface ChangePasswordFormProps {
  onChangePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  onCancel?: () => void;
  isFirstLogin?: boolean;
  compact?: boolean;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  onChangePassword,
  onCancel,
  isFirstLogin = false,
  compact = false,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});
  const { getPasswordRequirements, validatePassword, loading: settingsLoading } = useSecuritySettings();

  const requirements = useMemo(() => getPasswordRequirements(), [getPasswordRequirements]);

  const passwordStrength = useMemo(() => {
    return requirements.map((req, i) => ({
      id: `req-${i}`,
      label: req.label,
      met: req.check(newPassword),
    }));
  }, [newPassword, requirements]);

  const allRequirementsMet = passwordStrength.every(req => req.met);

  const validateForm = () => {
    const newErrors: { newPassword?: string; confirmPassword?: string } = {};
    
    const result = validatePassword(newPassword);
    if (!result.valid) {
      newErrors.newPassword = result.errors[0];
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords don't match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const { error } = await onChangePassword(newPassword);
    setIsSubmitting(false);

    if (error) {
      setErrors({ newPassword: error.message });
    }
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="newPassword">New Password</Label>
        <Input
          id="newPassword"
          type="password"
          placeholder="••••••••"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={isSubmitting}
          autoComplete="new-password"
        />
        {errors.newPassword && (
          <p className="text-sm text-destructive">{errors.newPassword}</p>
        )}
        
        {/* Password strength indicator */}
        <div className="space-y-1.5 pt-2">
          <p className="text-xs font-medium text-muted-foreground">Password requirements:</p>
          <div className="grid grid-cols-1 gap-1">
            {passwordStrength.map((req) => (
              <div key={req.id} className="flex items-center gap-2 text-xs">
                {req.met ? (
                  <Check className="w-3.5 h-3.5 text-primary" />
                ) : (
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                )}
                <span className={cn(
                  req.met ? 'text-primary' : 'text-muted-foreground'
                )}>
                  {req.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm Password</Label>
        <Input
          id="confirmPassword"
          type="password"
          placeholder="••••••••"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isSubmitting}
          autoComplete="new-password"
        />
        {errors.confirmPassword && (
          <p className="text-sm text-destructive">{errors.confirmPassword}</p>
        )}
      </div>
      <div className="flex gap-2">
        {onCancel && (
          <Button 
            type="button" 
            variant="outline" 
            className="flex-1"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        )}
        <Button 
          type="submit" 
          className="flex-1" 
          disabled={isSubmitting || !allRequirementsMet || settingsLoading}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Updating...
            </>
          ) : (
            'Update Password'
          )}
        </Button>
      </div>
    </form>
  );

  if (compact) {
    return formContent;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-workspace p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <LayoutTemplate className="w-8 h-8 text-primary" />
            <span className="text-2xl font-bold">LabelBatch</span>
          </div>
          <div className="flex justify-center mb-2">
            <KeyRound className="w-12 h-12 text-primary" />
          </div>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>
            {isFirstLogin 
              ? 'Please set a new password to continue'
              : 'Enter your new password below'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {formContent}
        </CardContent>
      </Card>
    </div>
  );
};
